import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, formatApiError } from "./AuthContext";
import { Trash2, Download } from "lucide-react";

export default function AdminSubscribers() {
  const { API, authHeaders, token } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, { headers: authHeaders });
      setSubs(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this reader from the list?")) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, { headers: authHeaders });
      setSubs((s) => s.filter((x) => x.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const onExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/subscribers/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "gentlegito-subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary">Journal readers</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-light">Subscribers</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {subs.length} {subs.length === 1 ? "quiet reader" : "quiet readers"}.
          </p>
        </div>
        <button
          data-testid="export-csv"
          onClick={onExport}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-sm hover:bg-secondary"
        >
          <Download size={16} strokeWidth={1.5} />
          Export CSV
        </button>
      </div>

      {error && <p className="mb-6 text-sm italic font-serif text-destructive">{error}</p>}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : subs.length === 0 ? (
        <p className="text-sm italic font-serif text-muted-foreground">
          No one has joined the journal yet.
        </p>
      ) : (
        <div className="bg-white/70 border border-border/60 rounded-3xl overflow-hidden">
          {subs.map((s, i) => (
            <div
              key={s.id}
              data-testid={`subscriber-${s.id}`}
              className={`flex items-center gap-4 p-5 sm:p-6 ${i > 0 ? "border-t border-border/60" : ""}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm sm:text-base text-foreground truncate">{s.email}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  Joined {new Date(s.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
              <button
                data-testid={`delete-sub-${s.id}`}
                onClick={() => onDelete(s.id)}
                className="p-2 text-foreground/60 hover:text-destructive"
                title="Remove"
              >
                <Trash2 size={16} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
